import { getAPI } from "@/axios-api";

/**
 * Fetch all phishing/smishing campaigns.
 * @returns {Promise<Array>} List of campaigns.
 */
export async function fetchCampaigns(page = 0, limit = 50) {
  try {
    const response = await getAPI.get('other/campaign', {
      params: {
        offset: page * limit,
        limit,
      }
    });
    if (response.status === 200) {
      // Combine both campaign types from the API response and add type property
      return {
        campaigns: [
          //...(response.data.data.smshing || []).map(campaign => ({ ...campaign, type: 'smshing' })),
          //...(response.data.data.phishing || []).map(campaign => ({ ...campaign, type: 'phishing' }))
          ...(response.data.results || []).map(campaign => ({ ...campaign, type: campaign?.campaign_type == 'phishing' ? 'phishing' : 'smshing' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        totalRecords: response.data.count,
        pagination : {
          next : response.data.next,
          previous: response.data.previous
        }
      };
    }
    return [];
  } catch (error) {
    return [];
  }
}

/**
 * Fetch phishing declaration page templates.
 * @returns {Promise<Array>} List of formatted templates.
 */
export async function fetchPhishingTemplates() {
  try {
    const response = await getAPI.get('phishing/declaration-page-template/');
    if (response.status === 200) {
      const templateData = response.data.data;
      const formattedTemplates = [];
      
      // Process template data
      for (let i = 0; i < templateData.length; i++) {
        // Push template details safely
        if (templateData[i] && templateData[i].name_template) {
          formattedTemplates.push({
            details: templateData[i]?.template_details,
            template_id: templateData[i].template_id,
            landing_page_id: templateData[i].landing_page_id,
            name: templateData[i].name_template,
            subject: templateData[i].subject_template,
            content: templateData[i].content_template,
            // Add default image for templates
            image: templateData[i]?.template_image || 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?q=80&w=1780&auto=format&fit=crop'
          });
        }
      }
      
      return formattedTemplates;
    }
    return [];
  } catch (error) {
    console.error('Error fetching template data: ', error);
    return [];
  }
}

/**
 * Create a new campaign (phishing or smishing).
 * @param {Object} campaignData - The campaign data to submit.
 * @returns {Promise<Object>} The created campaign data.
 */
export async function createCampaign(campaignData) {
  const { type, ...data } = campaignData;
  const endpoint = type === 'phishing' ? 'phishing/campaign/' : 'smshing/campaign/';
  
  try {
    const response = await getAPI.post(endpoint, data);
    return response.data;
  } catch (error) {
    console.error(`Error creating ${type} campaign:`, error);
    throw error;
  }
}


/**
 * Fetch a single group by ID
 * @param {string|number} groupId - The ID of the group to fetch
 * @returns {Promise<Object|null>} The group data or null if not found
 */
export async function fetchGroupById(groupId) {
  try {
    const response = await getAPI.get(`groupes/${groupId}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching group with ID ${groupId}:`, error);
    return null;
  }
}

/**
 * @param {string|number } groupId  the id of campaign to fetch
 * @returns {Promise<Object|null>}  the campaign data or null if not found
 */
export async function fetchCampaignById(campaignId) {
  try {
    const response = await getAPI.get(`smshing/campaign/${campaignId}/`);
    if (response.status === 200) {
      return response.data.data.detail;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching campaign with ID ${campaignId}:`, error);
    return null;
  }
}

/**
 * Download campaign report (PDF)
 * @param {Object} campaign - The campaign object containing id, name, and type
 * @returns {Promise<void>} Downloads the PDF report
 */
export async function downloadCampaignReport(campaign) {
  try {
    let endpoint;
    
    // Determine the correct endpoint based on campaign type
    if (campaign.type === 'phishing') {
      endpoint = `phishing/report/${campaign.id}/`;
    } else {
      endpoint = `smshing/campaigns/report/${campaign.id}/`;
    }
    
    const response = await getAPI.get(endpoint, { responseType: 'arraybuffer' });
    
    if (response.status === 200 && response.data) {
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${campaign.name}_rapport.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error downloading campaign report:', error);
    throw error;
  }
}

/**
 * Delete a campaign by ID
 * @param {string|number} campaignId - The ID of the campaign to delete
 * @param {string} type - The type of campaign ('phishing' or 'smishing')
 * @returns {Promise<Object>} Response data from the server
 */
export async function deleteCampaign(campaignId, type) {
  try {
    const endpoint = type === 'phishing' 
      ? `phishing/campaign/${campaignId}/`
      : `smshing/campaign/${campaignId}/`;
      
    const response = await getAPI.delete(endpoint);
    return response;
  } catch (error) {
    console.error(`Error deleting ${type} campaign with ID ${campaignId}:`, error);
    throw error;
  }
}
